import React from 'react';
import AutoComplete, { AutoCompleteProps } from 'antd/lib/auto-complete';
import styles from './Autosuggest.module.css';

export const prepareSuggestionsWithKeys = (suggestions: any) => {
  return suggestions.map((suggestion: any) => ({
    ...suggestion,
    key: suggestion.value + suggestion.label,
  }));
};

type OnSearchFunc = (value: string) => void;
type OnChangeFunc = (value: any, option: any[number] | any) => void;
type OnSelectFunc = (value: any, option: any[number]) => void;
export interface IAutoSuggestProps {
  startValue?: string;
  clearValue?: boolean;
  clearedValueCallback?: () => any;
  changeHandler?: (value: any, option: any) => any;
  onBlurHandler?: any;
  fetchFunction?: any;
  renderOptionsHandler?: (suggestions: any[]) => any;
  resultDataTransformer?: (result: any) => any;
  id: string;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  allowClear?: boolean;
  useCustomStyles?: boolean;
  useCustomOptions?: boolean;
  useCustomOptionsNewDesign?: boolean;
  hasSecondaryValueSelected?: boolean;
  customOptionLabelField?: string;
  showValidation?: boolean;
  itemRemovedTrigger?: boolean;
}

export interface IAutoSuggestState {
  value?: string | null;
  suggestions: any[];
}

class AutoSuggest extends React.Component<IAutoSuggestProps, IAutoSuggestState> {
  constructor(props: IAutoSuggestProps) {
    super(props);
    this.state = {
      value: null,
      suggestions: [],
    };
  }

  componentDidUpdate(prevProps: IAutoSuggestProps) {
    const { clearValue, clearedValueCallback, startValue, itemRemovedTrigger } = this.props;
    if (itemRemovedTrigger && typeof startValue === 'string' && this.state.value !== startValue) {
      this.setState({ value: startValue, suggestions: [] });
    }
    if (typeof startValue === 'string' && this.state.value === null) {
      this.setState({ value: startValue, suggestions: [] });
    }
    if (clearValue) {
      this.setState({ value: '', suggestions: [] });
      if (clearedValueCallback) {
        clearedValueCallback();
      }
    }
  }

  componentDidMount() {
    if (this.props.startValue && this.state.value === null) {
      this.setState({ value: this.props.startValue, suggestions: [] });
    }
  }

  getValueForState = (value: any, option: any) => {
    const { useCustomOptions, customOptionLabelField } = this.props;
    let valForState: string | null = value;
    if (useCustomOptions && customOptionLabelField && option?.label) {
      valForState = JSON.parse(option.value)[customOptionLabelField];
    } else if (option?.label) {
      valForState = option.label;
    }
    return valForState;
  };

  onChange: OnChangeFunc = (value: any, option: any) => {
    const { changeHandler } = this.props;
    if (changeHandler) {
      changeHandler(value, option);
    }
    this.setState({ value: this.getValueForState(value, option) });
  };

  onBlur = () => {
    const { value } = this.state;
    const { onBlurHandler, id } = this.props;
    const resetValue = this.props.startValue ? this.props.startValue : '';
    let shouldResetValue = false;
    if (onBlurHandler) {
      shouldResetValue = onBlurHandler({ id, value });
    }
    this.setState({
      value: shouldResetValue ? resetValue : this.state.value,
      suggestions: [],
    });
  };

  onSearch: OnSearchFunc = async (value) => {
    if (!value || value.length < 2) return false;
    const { fetchFunction, resultDataTransformer } = this.props;

    await fetchFunction(value)
      .then((response: any) => {
        let result = [...response];
        if (resultDataTransformer) {
          result = resultDataTransformer(result);
        }
        this.setState({ suggestions: result });
      })
      .catch((error: any) => {
        if (!error || error !== 'aborted') {
          throw new Error(error);
        }
      });
  };

  onSelect: OnSelectFunc = (value: any, option: any) => {
    const { changeHandler } = this.props;
    if (changeHandler) {
      changeHandler(value, option);
    }
    this.setState({
      value: this.getValueForState(value, option),
      suggestions: [],
    });
  };

  getClassNames() {
    const { className = '', useCustomStyles = false, showValidation = false, hasSecondaryValueSelected = false } = this.props;
    let clsNames = `${className} ${useCustomStyles ? styles.autocomplete : styles.autocomplete_nonCustom} ${showValidation ? styles.autocomplete_invalid : ''}`;
    if (hasSecondaryValueSelected) {
      clsNames += useCustomStyles ? styles.autocomplete_custom_hasSecondaryValueSelected : styles.autocomplete_hasSecondaryValueSelected;
    }
    return clsNames;
  }

  render() {
    const { value, suggestions } = this.state;
    const {
      id,
      disabled = false,
      placeholder = '',
      allowClear = false,
      useCustomOptions = false,
      renderOptionsHandler = prepareSuggestionsWithKeys,
      useCustomOptionsNewDesign = false,
    } = this.props;

    const autocompleteProps: AutoCompleteProps = {
      className: this.getClassNames(),
      placeholder,
      allowClear,
      id,
      value: value === null ? '' : value,
      disabled,
      options: renderOptionsHandler(suggestions),
      onSearch: this.onSearch,
      onChange: this.onChange,
      onSelect: this.onSelect,
      onBlur: this.onBlur,
    };

    if (useCustomOptions) {
      autocompleteProps.popupClassName = styles.hasCustomOptions;
    }
    if (useCustomOptionsNewDesign) {
      autocompleteProps.popupClassName = styles.hasCustomOptionsNewDesign;
    }

    return <AutoComplete {...autocompleteProps} />;
  }
}

export default AutoSuggest;
